import type {
  ActiveBotEntity,
  BotRenderSnapshot,
} from "../application/bot-runtime";
import type {
  PlayerDrawableState,
  PlayerRenderSnapshot,
} from "../application/player-runtime";
import type { PlayerBodyTrailState } from "../simulation/player-body-trail";
import type { Vector2 } from "../simulation/player-movement";

export interface InterpolatedRenderFrame {
  readonly alpha: number;
  readonly bots: readonly ActiveBotEntity[];
  readonly player: PlayerDrawableState | null;
  readonly simulationTimeMs: number;
}

export const calculateRenderInterpolationAlpha = (
  remainderMs: number,
  fixedStepMs: number,
): number => {
  if (!Number.isFinite(remainderMs)) {
    throw new RangeError("Render remainder must be finite");
  }
  if (!Number.isFinite(fixedStepMs) || fixedStepMs <= 0) {
    throw new RangeError("Render fixed step must be positive and finite");
  }
  return Math.min(1, Math.max(0, remainderMs / fixedStepMs));
};

const interpolateScalar = (
  previous: number,
  current: number,
  alpha: number,
): number => previous + (current - previous) * alpha;

const interpolateVector = (
  previous: Vector2,
  current: Vector2,
  alpha: number,
): Vector2 => ({
  x: interpolateScalar(previous.x, current.x, alpha),
  y: interpolateScalar(previous.y, current.y, alpha),
});

export const interpolateHeading = (
  previous: number,
  current: number,
  alpha: number,
): number => {
  const difference = Math.atan2(
    Math.sin(current - previous),
    Math.cos(current - previous),
  );
  return previous + difference * alpha;
};

const interpolateBody = (
  previous: PlayerBodyTrailState,
  current: PlayerBodyTrailState,
  alpha: number,
): PlayerBodyTrailState => ({
  path: current.path.map((point, index) =>
    interpolateVector(previous.path[index] ?? point, point, alpha),
  ),
  rippleStartedAtMs: current.rippleStartedAtMs,
  segments: current.segments.map((segment, index) => ({
    position: interpolateVector(
      previous.segments[index]?.position ?? segment.position,
      segment.position,
      alpha,
    ),
    radius: segment.radius,
  })),
});

const interpolatePlayer = (
  previous: PlayerDrawableState | null,
  current: PlayerDrawableState | null,
  alpha: number,
): PlayerDrawableState | null => {
  if (current === null) {
    return null;
  }
  if (previous === null) {
    return current;
  }
  return {
    body: interpolateBody(previous.body, current.body, alpha),
    player: {
      ...current.player,
      directionRadians: interpolateHeading(
        previous.player.directionRadians,
        current.player.directionRadians,
        alpha,
      ),
      position: interpolateVector(
        previous.player.position,
        current.player.position,
        alpha,
      ),
    },
  };
};

const interpolateBots = (
  snapshot: BotRenderSnapshot,
  alpha: number,
): readonly ActiveBotEntity[] => {
  const previousById = new Map(snapshot.previous.map((bot) => [bot.id, bot]));
  return snapshot.current.map((bot) => {
    const previous = previousById.get(bot.id);
    if (previous === undefined) {
      return bot;
    }
    return {
      ...bot,
      body: interpolateBody(previous.body, bot.body, alpha),
      directionRadians: interpolateHeading(
        previous.directionRadians,
        bot.directionRadians,
        alpha,
      ),
      position: interpolateVector(previous.position, bot.position, alpha),
    };
  });
};

export const interpolateRenderFrame = (
  playerSnapshot: PlayerRenderSnapshot,
  botSnapshot: BotRenderSnapshot,
): InterpolatedRenderFrame => {
  const alpha = calculateRenderInterpolationAlpha(
    playerSnapshot.remainderMs,
    playerSnapshot.fixedStepMs,
  );
  return {
    alpha,
    bots: interpolateBots(botSnapshot, alpha),
    player: interpolatePlayer(
      playerSnapshot.previous,
      playerSnapshot.current,
      alpha,
    ),
    simulationTimeMs:
      playerSnapshot.simulationTimeMs + playerSnapshot.remainderMs,
  };
};
